import { memo } from 'react';

import {
  DATE_FORMAT_LONG_FNS,
  DATE_FORMAT_LONG_MINUTE,
  SYSTEM_DATE_FORMAT_SHORT_FNS,
} from 'CorePackage/MainModule/constants/dateFormats';
import { IKnmPreview } from 'APIPackage/KnmSearchModule/interfaces/IKnmPreview';
import { formatDate, formatDateLocale } from 'CorePackage/MainModule/common/formatDate';

import styles from './KnmPreview.module.scss';
import { KnmSubtitle } from './KnmSubtitle';
import { convertArrayToDate } from '../common/convertArrayToDate';
import { parseErknmValue } from '../common/parseErknmValue';
import { SubjectNotifications } from './SubjectNotifications';
import { EpguNotifications } from './EpguNotifications';
import { getSuspensionEvents } from '../common/getSuspensionEvents';
import { getPeriodExtension } from '../common/getPeriodExtension';
import { getFullName } from '../common/getFullName';

interface IPmCommonProps {
  passportData: IKnmPreview;
}

export const PmCommon = memo(({ passportData }: IPmCommonProps) => {
  const suspensionEvents = getSuspensionEvents(passportData, 'pmErknm');
  const periodExtension = getPeriodExtension(passportData, 'pmErknm');

  const signer =
    passportData.pmErknm?.signers && passportData.pmErknm.signers.length > 0
      ? {
          fioSigner: passportData.pmErknm.signers[0].fioSigner,
          titleSigner: passportData.pmErknm.signers[0].titleSigner,
        }
      : {
          fioSigner: passportData.pmErknm?.officialFullName,
          titleSigner: passportData.pmErknm?.warningContentCreatorPosition,
        };

  return (
    <div className={styles.ColumnContent}>
      <KnmSubtitle title="Сведения о профилактическом мероприятии">
        {passportData.erpId && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Номер электронного паспорта ПМ</div>
            <div className={styles.ColValue}>{passportData.erpId}</div>
          </div>
        )}
        {passportData.pmErknm && passportData.pmErknm.pmType && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Вид профилактического мероприятия</div>
            <div className={styles.ColValue}>{parseErknmValue(passportData.pmErknm.pmType.value)?.title}</div>
          </div>
        )}
        {passportData.startDate && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Дата начала ПМ</div>
            <div className={styles.ColValue}>
              {formatDateLocale({
                date: convertArrayToDate(passportData.startDate),
                dateFormat: DATE_FORMAT_LONG_FNS,
                parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
              })}
            </div>
          </div>
        )}
        {signer.fioSigner && (
          <div className={styles.Row}>
            <div className={styles.ColText}>ФИО должностного лица</div>
            <div className={styles.ColValue}>{signer.fioSigner}</div>
          </div>
        )}
        {signer.titleSigner && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Должность лица, сформировавшего содержание предостережения</div>
            <div className={styles.ColValue}>{signer.titleSigner}</div>
          </div>
        )}
        {passportData.stopDate && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Дата окончания ПМ</div>
            <div className={styles.ColValue}>
              {formatDateLocale({
                date: convertArrayToDate(passportData.stopDate),
                dateFormat: DATE_FORMAT_LONG_FNS,
                parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
              })}
            </div>
          </div>
        )}
        {passportData?.pmErknm.pmForm?.title && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Форма проведения профилактического мероприятия</div>
            <div className={styles.ColValue}>{passportData.pmErknm.pmForm.title}</div>
          </div>
        )}
        {passportData.pmErknm && passportData.pmErknm.location && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Место проведения профилактического мероприятия</div>
            <div className={styles.ColValue}>{passportData.pmErknm.location}</div>
          </div>
        )}
      </KnmSubtitle>
      {!!periodExtension.length && (
        <KnmSubtitle title="Сведения о продлении срока проведения ПМ">
          {periodExtension.map((item, index) => (
            <div key={index}>
              <span className={styles.SectionTitle}>Решение о продлении № {index + 1}</span>
              {item.event && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Действие, на основе которого срок проведения ПМ продлен</div>
                  <div className={styles.ColValue}>{item.event}</div>
                </div>
              )}
              {item.decisionDateTime && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата и время издания решения</div>
                  <div className={styles.ColValue}>{item.decisionDateTime}</div>
                </div>
              )}
              {item.decisionNumber && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Номер решения</div>
                  <div className={styles.ColValue}>{item.decisionNumber}</div>
                </div>
              )}
              {item.amountDays && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Продление срока проведения ПМ</div>
                  <div className={styles.ColValue}>{item.amountDays}</div>
                </div>
              )}
            </div>
          ))}
        </KnmSubtitle>
      )}
      {!!suspensionEvents.length && (
        <KnmSubtitle title="Сведения о приостановлении срока проведения КНМ">
          {suspensionEvents.map((item, index) => (
            <div key={index}>
              <div className={styles.SectionTitle}>{`Решение о приостановлении № ${item.decisionNumber}`}</div>
              {item.event && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Действие, на основе которого срок проведения ПМ приостановлен</div>
                  <div className={styles.ColValue}>{item.event}</div>
                </div>
              )}
              {item.decisionDateTime && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата и время издания решения</div>
                  <div className={styles.ColValue}>{item.decisionDateTime}</div>
                </div>
              )}
              {item.decisionNumber && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Номер решения</div>
                  <div className={styles.ColValue}>{item.decisionNumber}</div>
                </div>
              )}
              {item.startDate && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата начала приостановления срока ПМ</div>
                  <div className={styles.ColValue}>{item.startDate}</div>
                </div>
              )}
              {item.stopDate && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата окончания приостановления срока ПМ</div>
                  <div className={styles.ColValue}>{item.stopDate}</div>
                </div>
              )}
            </div>
          ))}
        </KnmSubtitle>
      )}
      {passportData.pmErknm && passportData.pmErknm.noteWarning && (
        <KnmSubtitle title="Сведения о содержании предостережения">
          {passportData.erpId && (
            <div className={styles.Row}>
              <div className={styles.ColText}>Описание предостережения</div>
              <div className={styles.ColValue}>{passportData.pmErknm.noteWarning}</div>
            </div>
          )}
        </KnmSubtitle>
      )}
      {passportData.pmErknm?.reasons && passportData.pmErknm.reasons.length > 0 && (
        <KnmSubtitle title="Основание проведения ПМ">
          {passportData.pmErknm.reasons.map((item, index) => (
            <div key={index}>
              <span className={styles.SectionTitle}>Основание проведения ПМ #{index + 1}</span>
              {item.reason && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Наименование</div>
                  <div className={styles.ColValue}>{item.reason.type.name}</div>
                </div>
              )}
              {item.riskIndicators?.map((indicatorValue, indicatorIndex) => {
                return (
                  <>
                    {indicatorValue.riskIndicator && (
                      <div key={indicatorIndex}>
                        <span className={styles.SectionTitle}>Индикатор риска #{indicatorIndex + 1}</span>
                        <div className={styles.Row}>
                          <div className={styles.ColValue}>
                            {parseErknmValue(indicatorValue.riskIndicator.value)?.title}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                );
              })}
              {item.order && (
                <>
                  {item.order.guarantors.map((guarantor, guarantorIndex) => {
                    return (
                      <div className={styles.Row} key={guarantorIndex}>
                        <div className={styles.ColText}>ФИО лица, издавшего поручение</div>
                        <div className={styles.ColValue}>{getFullName(guarantor)}</div>
                      </div>
                    );
                  })}
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Номер поручения</div>
                    <div className={styles.ColValue}>{item.order.number}</div>
                  </div>
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Дата поручения</div>
                    <div className={styles.ColValue}>
                      {formatDate({
                        date: item.order.date,
                        dateFormat: DATE_FORMAT_LONG_MINUTE,
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </KnmSubtitle>
      )}
      {passportData?.pmErknm?.requirements && passportData?.pmErknm.requirements.length > 0 && (
        <KnmSubtitle title="Предмет профилактического мероприятия">
          {passportData?.pmErknm.requirements.map((npa, index) => (
            <div key={index}>
              <span className={styles.SectionTitle}>Обязательные требования, подлежащие проверке #{index + 1}</span>
              <div className={styles.Row}>
                <div className={styles.ColText}>Нормативно-правовой акт</div>
                <div className={styles.ColValue}>{npa.title}</div>
              </div>
              <div className={styles.Row}>
                <div className={styles.ColText}>Номер</div>
                <div className={styles.ColValue}>{npa.number}</div>
              </div>
              <div className={styles.Row}>
                <div className={styles.ColText}>Дата утверждения</div>
                <div className={styles.ColValue}>
                  {formatDateLocale({
                    date: convertArrayToDate(npa.acceptanceDate),
                    dateFormat: DATE_FORMAT_LONG_FNS,
                    parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
                  })}
                </div>
              </div>
              <div className={styles.Row}>
                <div className={styles.ColText}>Структурная единица</div>
                <div className={styles.ColValue}>{npa.structuralUnit.title}</div>
              </div>
              {npa.structuralUnit.mandatoryRequirements.map((requirement, index1) => (
                <div key={index1} className={styles.Row}>
                  <div className={styles.ColText}>Содержание требования #{index1 + 1}</div>
                  <div className={styles.ColValue}>{requirement.title}</div>
                </div>
              ))}
            </div>
          ))}
        </KnmSubtitle>
      )}
      {passportData?.pmErknm.approveDocs && passportData.pmErknm.approveDocs.length > 0 && (
        <KnmSubtitle title="Разрешительные и иные документы">
          {passportData?.pmErknm.approveDocs.map((item, index) => (
            <div className={styles.SectionBlock} key={index}>
              {item.approveDoc && item.approveDoc.value && (
                <>
                  <div className={styles.SectionTitle}>Документ #{index + 1}</div>
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Наименование документа</div>
                    <div className={styles.ColValue}>{parseErknmValue(item.approveDoc.value)?.title}</div>
                  </div>
                </>
              )}
            </div>
          ))}
        </KnmSubtitle>
      )}
      <SubjectNotifications subjectNotifications={passportData?.subjectNotifications} isPm />
      <EpguNotifications epguNotifications={passportData?.epguNotifications} />
      {passportData?.pmErknm?.pmRejects && passportData.pmErknm.pmRejects.length !== 0 && (
        <KnmSubtitle title="Информация о непроведении (несогласии) ПМ">
          {passportData.pmErknm.pmRejects.map((item, index) => (
            <div key={index}>
              <div className={styles.SectionTitle}>Причина #{index + 1}</div>
              {item.date && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата выявления причины</div>
                  <div className={styles.ColValue}>
                    {formatDateLocale({
                      date: convertArrayToDate(item.date),
                      dateFormat: DATE_FORMAT_LONG_FNS,
                      parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
                    })}
                  </div>
                </div>
              )}
              {item.pmCanceledReason?.name && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Причина непроведения профилактического мероприятия</div>
                  <div className={styles.ColValue}>{item.pmCanceledReason.name}</div>
                </div>
              )}
            </div>
          ))}
        </KnmSubtitle>
      )}
    </div>
  );
});
